import { useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  Alert,
} from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  TestPhoto,
  TestPhotoType,
  uploadTestPhoto,
  deleteTestPhoto,
} from "../api/patients";
import { SecureImage, SecureThumb } from "./SecureMedia";
import { useAuth } from "../auth/AuthContext";

interface Props {
  patientId: string;
  testType: TestPhotoType;
  photos: TestPhoto[];
  onPhotosUpdated: (photos: TestPhoto[]) => void;
}

export default function TestPhotosInline({
  patientId,
  testType,
  photos,
  onPhotosUpdated,
}: Props) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.is_superuser === true;
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleUpload = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      setError("Файл должен быть изображением");
      return;
    }
    setError("");
    setUploading(true);
    try {
      const result = await uploadTestPhoto(patientId, testType, file, file.name);
      const newPhoto: TestPhoto = {
        url: result.file_url,
        thumb_url: result.thumb_url,
        sequence: result.sequence,
        uploaded_by: "",
        uploaded_by_email: result.uploaded_by_email,
        uploaded_at: new Date().toISOString(),
        file_name: file.name,
      };
      onPhotosUpdated([...photos, newPhoto]);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Ошибка загрузки");
    } finally {
      setUploading(false);
      if (cameraInputRef.current) cameraInputRef.current.value = "";
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
  };

  const openViewer = (idx: number) => setViewerIndex(idx);
  const closeViewer = () => setViewerIndex(null);
  const prevPhoto = () => {
    if (viewerIndex === null) return;
    setViewerIndex((viewerIndex - 1 + photos.length) % photos.length);
  };
  const nextPhoto = () => {
    if (viewerIndex === null) return;
    setViewerIndex((viewerIndex + 1) % photos.length);
  };

  const currentPhoto = viewerIndex !== null ? photos[viewerIndex] : null;

  const handleDelete = async () => {
    if (!currentPhoto) return;
    setDeleting(true);
    try {
      await deleteTestPhoto(patientId, testType, currentPhoto.sequence);
      const updated = photos.filter((p) => p.sequence !== currentPhoto.sequence);
      onPhotosUpdated(updated);
      setConfirmDeleteOpen(false);
      setViewerIndex(null);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Ошибка удаления");
      setConfirmDeleteOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: photos.length > 0 ? 1 : 0 }}>
        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <PhotoCameraIcon />}
          onClick={() => cameraInputRef.current?.click()}
          disabled={uploading}
        >
          Фото
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AttachFileIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          Файл
        </Button>
      </Box>

      {photos.length > 0 && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {photos.map((photo, idx) => (
            <SecureThumb
              key={photo.sequence}
              url={photo.thumb_url || photo.url}
              onClick={() => openViewer(idx)}
              sx={{
                width: 56,
                height: 56,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                backgroundSize: "cover",
                backgroundPosition: "center",
                cursor: "pointer",
                transition: "transform 0.15s, border-color 0.15s, box-shadow 0.15s",
                "&:hover": {
                  borderColor: "primary.main",
                  transform: "scale(1.05)",
                  boxShadow: 2,
                },
              }}
            />
          ))}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 0.5, py: 0, fontSize: "0.75rem" }}>
          {error}
        </Alert>
      )}

      <Dialog
        open={viewerIndex !== null}
        onClose={closeViewer}
        fullScreen
        slotProps={{
          paper: {
            sx: { bgcolor: "rgba(0, 0, 0, 0.92)" },
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconButton
            onClick={closeViewer}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              color: "white",
              bgcolor: "rgba(255,255,255,0.1)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
              zIndex: 2,
            }}
          >
            <CloseIcon />
          </IconButton>

          {isAdmin && currentPhoto && (
            <IconButton
              onClick={() => setConfirmDeleteOpen(true)}
              sx={{
                position: "absolute",
                top: 12,
                left: 12,
                color: "white",
                bgcolor: "rgba(211, 47, 47, 0.7)",
                "&:hover": { bgcolor: "rgba(211, 47, 47, 0.9)" },
                zIndex: 2,
              }}
            >
              <DeleteIcon />
            </IconButton>
          )}

          {photos.length > 1 && (
            <>
              <IconButton
                onClick={prevPhoto}
                sx={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "white",
                  bgcolor: "rgba(255,255,255,0.1)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                  zIndex: 2,
                }}
                size="large"
              >
                <ChevronLeftIcon fontSize="large" />
              </IconButton>
              <IconButton
                onClick={nextPhoto}
                sx={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "white",
                  bgcolor: "rgba(255,255,255,0.1)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                  zIndex: 2,
                }}
                size="large"
              >
                <ChevronRightIcon fontSize="large" />
              </IconButton>
            </>
          )}

          {currentPhoto && (
            <SecureImage
              url={currentPhoto.medium_url || currentPhoto.url}
              alt={`Фото #${currentPhoto.sequence}`}
              sx={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          )}

          {currentPhoto && (
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                bottom: 16,
                left: "50%",
                transform: "translateX(-50%)",
                color: "white",
                bgcolor: "rgba(0,0,0,0.5)",
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              #{currentPhoto.sequence}
              {photos.length > 1 && ` — ${(viewerIndex ?? 0) + 1} из ${photos.length}`}
            </Typography>
          )}
        </Box>
      </Dialog>

      <Dialog open={confirmDeleteOpen} onClose={() => !deleting && setConfirmDeleteOpen(false)}>
        <DialogTitle>Удалить фото?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Фото #{currentPhoto?.sequence} будет удалено навсегда.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} disabled={deleting}>
            Отмена
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {deleting ? "Удаление..." : "Удалить"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
